/**
 * Pure helpers for Item-audit location lists: which API slice forms the transfer pool
 * and how that pool is filtered after moving rows between the available and job grids.
 *
 * Must stay aligned with the Item branch of `onRequestEnd` in the manage-audit modal
 * (Suggested → RecentStockLocationList, All → OtherLocationList).
 */

/** Radio / state values for location filter in the modal. */
export type LocationFilterType = "suggested" | "other";

/** First element of `response.data.Data` for Item audit location reads. */
export interface ItemAuditLocationDataSlice {
    RecentStockLocationList?: LocationRow[];
    OtherLocationList?: LocationRow[];
}

/** Minimal row shape for pool operations (API locations use `Id`). */
export interface LocationRow {
    Id: string;
}

/**
 * Returns the location array used as the transfer pool for `responseLocationGridData`.
 * This must match how the left grid is derived for Item audits in `onRequestEnd`.
 *
 * @param dataItem0 - `Data[0]` from an Item audit location response, or null/undefined.
 * @param locationType - `suggested` uses recent/suggested stock locations; `other` uses all other locations.
 * @returns A new array reference is not guaranteed; callers may spread when storing in React state.
 */
export function getItemLocationPoolFromResponse(
    dataItem0: ItemAuditLocationDataSlice | null | undefined,
    locationType: LocationFilterType
): LocationRow[] {
    if (!dataItem0) {
        return [];
    }
    if (locationType === "suggested") {
        return dataItem0.RecentStockLocationList || [];
    }
    return dataItem0.OtherLocationList || [];
}

/**
 * Rows that should remain on the left (available) grid after moving the selection to the job grid.
 * Excludes every id that is now on the job (existing job rows plus newly moved ids).
 *
 * @param pool - Same pool as `getItemLocationPoolFromResponse` / `responseLocationGridData`.
 * @param allIdsNowAssignedToJob - Union of job location ids and ids just moved from the left grid.
 */
export function computeLeftGridDataAfterMoveToJob(
    pool: LocationRow[],
    allIdsNowAssignedToJob: string[]
): LocationRow[] {
    return pool.filter((row) => !allIdsNowAssignedToJob.includes(row.Id));
}

/**
 * Rows for the left grid after moving non-required locations back from the job grid.
 * Keeps pool rows whose id is either returning from the job or already on the left grid.
 *
 * @param pool - Same pool as `responseLocationGridData`.
 * @param idsRemovedFromJob - Ids of rows removed from the job (non-required only, per modal rules).
 * @param currentLeftGridIds - Ids of rows that were already shown on the left grid before this operation.
 */
export function computeLeftGridDataAfterMoveFromJob(
    pool: LocationRow[],
    idsRemovedFromJob: string[],
    currentLeftGridIds: string[]
): LocationRow[] {
    const keepIds = new Set([...idsRemovedFromJob, ...currentLeftGridIds]);
    return pool.filter((row) => keepIds.has(row.Id));
}
